import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { getExpensesCollectionRefs, getApplicationCurrentBorrower } from 'selectors';
import {
  InfoTableWrapper,
  InfoTable,
  Input,
  RemoveButton,
  StyledStateAndLLCButton,
  StyledSelect,
} from './styles';

const ContactRow = memo(({
  contact,
  index,
  isMainContact,
  onInputChange,
  onRemove,
  onStateClick,
  onLlcClick,
  stateName,
  legalName,
  isValid,
  showError,
}) => {
  const refs = useSelector(getExpensesCollectionRefs);
  const stateOptions = refs.statesCollection ? refs.statesCollection.map((state) => ({
    value: state.value,
    label: state.label,
  })) : [];
  const currentBorrower = useSelector(getApplicationCurrentBorrower);
  const currentBorrowerName = currentBorrower && currentBorrower.displayName;
  const selectedStateOption = stateOptions.find((option) =>
    option.label === contact.state || option.value === contact.stateCode) || null;
  if (isMainContact) {
    return (
      <>
        <tr>
          <td>{currentBorrowerName}</td>
          <td>
            <StyledStateAndLLCButton onClick={onStateClick}>
              {stateName ? stateName.label : 'Select State'}
            </StyledStateAndLLCButton>
          </td>
          <td>
            <StyledStateAndLLCButton onClick={onLlcClick}>
              {legalName}
            </StyledStateAndLLCButton>
          </td>
          <td />
        </tr>
      </>
    );
  }

  return (
    <>
      <tr>
        <td>
          <Input
            type="text"
            placeholder="Full name"
            value={contact.fullName || ''}
            onChange={(e) => onInputChange(index, 'fullName', e.target.value)}
          />
        </td>
        <td>
          <StyledSelect invalid={!isValid && showError}>
            <Select
              classNamePrefix="react-select"
              placeholder="State"
              options={stateOptions}
              onChange={(selectedOption) => {
                onInputChange(index, 'state', selectedOption ? selectedOption.label : '');
                onInputChange(index, 'stateCode', selectedOption ? selectedOption.value : '');
              }}
              value={selectedStateOption}
              isClearable
              isSearchable
              menuPlacement="auto"
            />
          </StyledSelect>
        </td>
        <td>
          <Input
            type="text"
            placeholder="LLC Name"
            value={contact.llc || ''}
            onChange={(e) => onInputChange(index, 'llc', e.target.value)}
          />
        </td>
        <td>
          <RemoveButton type="button" onClick={() => onRemove(index)}>
            ×
          </RemoveButton>
        </td>
      </tr>
    </>
  );
});

ContactRow.displayName = 'ContactRow';

ContactRow.propTypes = {
  contact: PropTypes.shape({
    fullName: PropTypes.string,
    state: PropTypes.string,
    stateCode: PropTypes.string,
    llc: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  isMainContact: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onStateClick: PropTypes.func.isRequired,
  onLlcClick: PropTypes.func.isRequired,
  stateName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ]),
  legalName: PropTypes.string,
  isValid: PropTypes.bool.isRequired,
  showError: PropTypes.bool.isRequired,
};

ContactRow.defaultProps = {
  stateName: null,
  legalName: '',
};

const ContactsTable = memo(({
  contacts,
  onInputChange,
  onRemoveContact,
  onStateClick,
  onLlcClick,
  stateName,
  legalName,
  isContactValid,
  showError,
}) => (
  <InfoTableWrapper>
    <InfoTable>
      <thead>
        <tr>
          <th>Full name</th>
          <th>State</th>
          <th>LLC Name</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {contacts.map((contact, index) => {
          const contactIsValid = isContactValid(contact);
          const contactKey = contact.id || index;

          return (
            <ContactRow
              key={contactKey}
              contact={contact}
              index={index}
              isMainContact={index === 0}
              onInputChange={onInputChange}
              onRemove={onRemoveContact}
              onStateClick={onStateClick}
              onLlcClick={onLlcClick}
              stateName={stateName}
              legalName={legalName}
              isValid={contactIsValid}
              showError={showError}
            />
          );
        })}
      </tbody>
    </InfoTable>
  </InfoTableWrapper>
));

ContactsTable.displayName = 'ContactsTable';

ContactsTable.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.shape({
    fullName: PropTypes.string,
    state: PropTypes.string,
    llc: PropTypes.string,
  })).isRequired,
  onInputChange: PropTypes.func.isRequired,
  onRemoveContact: PropTypes.func.isRequired,
  onStateClick: PropTypes.func.isRequired,
  onLlcClick: PropTypes.func.isRequired,
  stateName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string,
    }),
  ]).isRequired,
  legalName: PropTypes.string.isRequired,
  isContactValid: PropTypes.func.isRequired,
  showError: PropTypes.bool.isRequired,
};

export default ContactsTable;
